import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import {
    FaCheck, FaClock, FaDoorOpen, FaConciergeBell, FaCalendarCheck,
    FaArrowRight, FaUserPlus, FaDumbbell, FaClipboardList, FaChartBar,
    FaDog, FaLock, FaPaw, FaStickyNote, FaCalendarPlus, FaChevronDown, FaChevronUp,
} from 'react-icons/fa';
import { MdRocketLaunch } from 'react-icons/md';
import { TrainerContext } from '../../context/TrainerContext';
import '../../styles/SetupChecklist.css';

const SCHOOL_SETUP_STEPS = [
    { key: 'workingHours', icon: FaClock, labelKey: 'setup.workingHours.label', descKey: 'setup.workingHours.description', doneLabelKey: 'setup.workingHours.doneLabel', route: '/trainer/account?tab=workingHours', btnTextKey: 'setup.workingHours.btnText' },
    { key: 'halls', icon: FaDoorOpen, labelKey: 'setup.halls.label', descKey: 'setup.halls.description', doneLabelKey: 'setup.halls.doneLabel', route: '/school/details?tab=dane-szkoly', btnTextKey: 'setup.halls.btnText', optional: true },
    { key: 'services', icon: FaConciergeBell, labelKey: 'setup.services.label', descKey: 'setup.services.description', doneLabelKey: 'setup.services.doneLabel', route: '/school/details?tab=uslugi', btnTextKey: 'setup.services.btnText' },
    { key: 'unlockedMonths', icon: FaCalendarCheck, labelKey: 'setup.unlockedMonths.label', descKey: 'setup.unlockedMonths.description', doneLabelKey: 'setup.unlockedMonths.doneLabel', route: '/school/details?tab=ustawienia', btnTextKey: 'setup.unlockedMonths.btnText' },
    { key: 'owners', icon: FaUserPlus, labelKey: 'setup.owners.label', descKey: 'setup.owners.description', doneLabelKey: 'setup.owners.doneLabel', route: '/owners/all', btnTextKey: 'setup.owners.btnText' },
    { key: 'dogs', icon: FaDog, labelKey: 'setup.dogs.label', descKey: 'setup.dogs.description', doneLabelKey: 'setup.dogs.doneLabel', route: '/podopieczni', btnTextKey: 'setup.dogs.btnText' },
    { key: 'exercises', icon: FaDumbbell, labelKey: 'setup.exercises.label', descKey: 'setup.exercises.description', doneLabelKey: 'setup.exercises.doneLabel', route: '/exercises', btnTextKey: 'setup.exercises.btnText' },
    { key: 'surveys', icon: FaClipboardList, labelKey: 'setup.surveys.label', descKey: 'setup.surveys.description', doneLabelKey: 'setup.surveys.doneLabel', dynamicRoute: (schoolId) => `/survey/school/${schoolId}/all`, btnTextKey: 'setup.surveys.btnText' },
    { key: 'reports', icon: FaChartBar, labelKey: 'setup.reports.label', descKey: 'setup.reports.description', doneLabelKey: 'setup.reports.doneLabel', route: '/reports', btnTextKey: 'setup.reports.btnText' },
];

const TRAINER_TASKS_STEPS = [
    { key: 'surveyAssigned', icon: FaClipboardList, labelKey: 'setup.surveyAssigned.label', descKey: 'setup.surveyAssigned.description', doneLabelKey: 'setup.surveyAssigned.doneLabel', dynamicRoute: (schoolId) => `/survey/school/${schoolId}/all`, btnTextKey: 'setup.surveyAssigned.btnText' },
    { key: 'reportAssigned', icon: FaChartBar, labelKey: 'setup.reportAssigned.label', descKey: 'setup.reportAssigned.description', doneLabelKey: 'setup.reportAssigned.doneLabel', route: '/reports', btnTextKey: 'setup.reportAssigned.btnText' },
    { key: 'exerciseAssigned', icon: FaDumbbell, labelKey: 'setup.exerciseAssigned.label', descKey: 'setup.exerciseAssigned.description', doneLabelKey: 'setup.exerciseAssigned.doneLabel', route: '/podopieczni', btnTextKey: 'setup.exerciseAssigned.btnText' },
    { key: 'trainerNoteAdded', icon: FaStickyNote, labelKey: 'setup.trainerNoteAdded.label', descKey: 'setup.trainerNoteAdded.description', doneLabelKey: 'setup.trainerNoteAdded.doneLabel', route: '/podopieczni', btnTextKey: 'setup.trainerNoteAdded.btnText' },
    { key: 'recommendationsAdded', icon: FaPaw, labelKey: 'setup.recommendationsAdded.label', descKey: 'setup.recommendationsAdded.description', doneLabelKey: 'setup.recommendationsAdded.doneLabel', route: '/podopieczni', btnTextKey: 'setup.recommendationsAdded.btnText' },
    { key: 'trainingBooked', icon: FaCalendarPlus, labelKey: 'setup.trainingBooked.label', descKey: 'setup.trainingBooked.description', doneLabelKey: 'setup.trainingBooked.doneLabel', route: '/calendar', btnTextKey: 'setup.trainingBooked.btnText' },
];

const SetupChecklist = () => {
    const { t } = useTranslation('school');
    const navigate = useNavigate();
    const { trainer } = useContext(TrainerContext);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [phase1Expanded, setPhase1Expanded] = useState(true);
    const [phase2Expanded, setPhase2Expanded] = useState(() => {
        return localStorage.getItem('setup_phase2_collapsed') !== 'true';
    });

    const schoolId = trainer?.schoolId;

    const getStepRoute = (step) => {
        if (step.dynamicRoute) return step.dynamicRoute(schoolId);
        return step.route;
    };

    const fetchStatus = useCallback(async () => {
        try {
            const token = localStorage.getItem('jwt');
            const response = await axios.get('/api/v1/trainer/setup-status', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setStatus(response.data);
        } catch (err) {
            console.error('Failed to fetch setup status:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    useEffect(() => {
        if (!trainer) return;
        fetchStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trainer]);

    useEffect(() => {
        const handleFocus = () => fetchStatus();
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [fetchStatus]);

    useEffect(() => {
        if (status?.trainerTasks?.unlocked) {
            setPhase1Expanded(false);
        }
    }, [status]);

    useEffect(() => {
        if (status?.trainerTasks?.complete && localStorage.getItem('setup_phase2_collapsed') !== 'true') {
            setPhase2Expanded(false);
            localStorage.setItem('setup_phase2_collapsed', 'true');
        }
    }, [status]);

    const isSchoolStepResolved = useCallback((step) => {
        if (!status?.schoolSetup) return false;
        if (step.key === 'halls') return status.schoolSetup.halls || status.schoolSetup.hallsSkipped;
        return status.schoolSetup[step.key];
    }, [status]);

    const togglePhase2 = () => {
        const next = !phase2Expanded;
        setPhase2Expanded(next);
        localStorage.setItem('setup_phase2_collapsed', next ? 'false' : 'true');
    };

    const handleSkipHall = async () => {
        try {
            const token = localStorage.getItem('jwt');
            await axios.put('/api/v1/trainer/setup-status/skip-hall', null, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            fetchStatus();
        } catch (err) {
            console.error('Failed to skip hall setup:', err);
        }
    };

    if (loading) {
        return (
            <div className="setup-checklist setup-checklist--loading">
                <div className="setup-checklist__spinner" />
            </div>
        );
    }

    if (!status) return null;

    const { schoolSetup, trainerTasks } = status;

    const phase1Resolved = SCHOOL_SETUP_STEPS.filter(step => isSchoolStepResolved(step)).length;
    const phase1Total = SCHOOL_SETUP_STEPS.length;

    const phase2Resolved = trainerTasks?.unlocked
        ? TRAINER_TASKS_STEPS.filter(step => trainerTasks[step.key]).length
        : 0;
    const phase2Total = TRAINER_TASKS_STEPS.length;

    const totalResolved = phase1Resolved + phase2Resolved;
    const totalSteps = phase1Total + phase2Total;
    const progressPercent = (totalResolved / totalSteps) * 100;

    const allDone = schoolSetup?.complete && trainerTasks?.complete;

    const nextSchoolStep = !schoolSetup?.complete
        ? SCHOOL_SETUP_STEPS.find(step => !isSchoolStepResolved(step))?.key
        : null;
    const nextTrainerStep = trainerTasks?.unlocked && !trainerTasks?.complete
        ? TRAINER_TASKS_STEPS.find(step => !trainerTasks[step.key])?.key
        : null;

    return (
        <div className="setup-checklist">
            {/* Header */}
            <div className="setup-checklist__header">
                <div className="setup-checklist__header-left">
                    <div className={`setup-checklist__icon ${allDone ? 'setup-checklist__icon--done' : ''}`}>
                        <MdRocketLaunch />
                    </div>
                    <div>
                        <h2 className="setup-checklist__title">
                            {allDone ? t('setup.allDone') : t('setup.configureSchool')}
                        </h2>
                        <p className="setup-checklist__subtitle">
                            {allDone
                                ? t('setup.allDoneSubtitle')
                                : t('setup.configureSubtitle')
                            }
                        </p>
                    </div>
                </div>
                <div className="setup-checklist__progress">
                    <span className="setup-checklist__progress-text">
                        {t('setup.progressOf', { done: totalResolved, total: totalSteps })}
                    </span>
                    <div className="setup-checklist__progress-bar">
                        <div
                            className="setup-checklist__progress-fill"
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Phase 1: School Setup */}
            <div className={`setup-checklist__phase ${schoolSetup?.complete ? 'setup-checklist__phase--done' : 'setup-checklist__phase--active'}`}>
                <div
                    className={`setup-checklist__phase-header ${schoolSetup?.complete ? 'setup-checklist__phase-header--done' : ''}`}
                    onClick={() => setPhase1Expanded(!phase1Expanded)}
                    role="button"
                    tabIndex={0}
                >
                    <div className="setup-checklist__phase-header-left">
                        <div className={`setup-checklist__phase-badge ${schoolSetup?.complete ? 'setup-checklist__phase-badge--done' : 'setup-checklist__phase-badge--active'}`}>
                            {schoolSetup?.complete ? <FaCheck size={10} /> : '1'}
                        </div>
                        <span className="setup-checklist__phase-title">{t('setup.phase1Title')}</span>
                        <span className="setup-checklist__phase-count">
                            {phase1Resolved}/{phase1Total}
                        </span>
                    </div>
                    <div className="setup-checklist__phase-toggle">
                        {phase1Expanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                    </div>
                </div>

                {phase1Expanded && (
                    <div className="setup-checklist__steps">
                        {SCHOOL_SETUP_STEPS.map((step) => {
                            const isDone = schoolSetup[step.key];
                            const isSkipped = step.key === 'halls' && schoolSetup.hallsSkipped && !isDone;
                            const isResolved = isSchoolStepResolved(step);
                            const isNext = step.key === nextSchoolStep;
                            const StepIcon = step.icon;
                            return (
                                <div
                                    key={step.key}
                                    className={`setup-checklist__step ${isDone ? 'setup-checklist__step--done' : ''} ${isSkipped ? 'setup-checklist__step--skipped' : ''} ${isNext ? 'setup-checklist__step--next' : ''}`}
                                    onClick={!isResolved ? () => navigate(getStepRoute(step)) : undefined}
                                    role={!isResolved ? 'button' : undefined}
                                    tabIndex={!isResolved ? 0 : undefined}
                                >
                                    <div className={`setup-checklist__check ${isDone ? 'setup-checklist__check--done' : isSkipped ? 'setup-checklist__check--skipped' : isNext ? 'setup-checklist__check--next' : 'setup-checklist__check--pending'}`}>
                                        {isDone ? <FaCheck size={14} /> : <StepIcon size={16} />}
                                    </div>
                                    <div className="setup-checklist__step-info">
                                        <p className="setup-checklist__step-label">
                                            {isDone ? t(step.doneLabelKey) : t(step.labelKey)}
                                        </p>
                                        <p className="setup-checklist__step-description">
                                            {isDone ? t('setup.done') : isSkipped ? t('setup.skipped') : t(step.descKey)}
                                        </p>
                                    </div>
                                    {!isResolved && (
                                        <div className="setup-checklist__step-actions">
                                            <button
                                                className={`setup-checklist__step-btn ${isNext ? 'setup-checklist__step-btn--primary' : ''}`}
                                                onClick={(e) => { e.stopPropagation(); navigate(getStepRoute(step)); }}
                                            >
                                                {t(step.btnTextKey)}
                                                <FaArrowRight size={11} />
                                            </button>
                                            {step.optional && (
                                                <button
                                                    className="setup-checklist__skip-btn"
                                                    onClick={(e) => { e.stopPropagation(); handleSkipHall(); }}
                                                >
                                                    {t('setup.skip')}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Phase 2: Trainer Tasks */}
            <div className={`setup-checklist__phase ${trainerTasks?.complete ? 'setup-checklist__phase--done' : trainerTasks?.unlocked ? 'setup-checklist__phase--active' : 'setup-checklist__phase--locked'}`}>
                <div
                    className={`setup-checklist__phase-header ${trainerTasks?.complete ? 'setup-checklist__phase-header--done' : !trainerTasks?.unlocked ? 'setup-checklist__phase-header--locked' : ''}`}
                    onClick={trainerTasks?.unlocked ? togglePhase2 : undefined}
                    role={trainerTasks?.unlocked ? 'button' : undefined}
                    tabIndex={trainerTasks?.unlocked ? 0 : undefined}
                >
                    <div className="setup-checklist__phase-header-left">
                        <div className={`setup-checklist__phase-badge ${trainerTasks?.complete ? 'setup-checklist__phase-badge--done' : trainerTasks?.unlocked ? 'setup-checklist__phase-badge--active' : 'setup-checklist__phase-badge--locked'}`}>
                            {trainerTasks?.complete ? <FaCheck size={10} /> : trainerTasks?.unlocked ? '2' : <FaLock size={10} />}
                        </div>
                        <span className="setup-checklist__phase-title">{t('setup.phase2Title')}</span>
                        {trainerTasks?.unlocked && (
                            <span className="setup-checklist__phase-count">
                                {phase2Resolved}/{phase2Total}
                            </span>
                        )}
                    </div>
                    {!trainerTasks?.unlocked && (
                        <span className="setup-checklist__phase-locked-hint">
                            <FaDog size={12} />
                            {t('setup.phase2LockedHint')}
                        </span>
                    )}
                    {trainerTasks?.unlocked && (
                        <div className="setup-checklist__phase-toggle">
                            {phase2Expanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                        </div>
                    )}
                </div>

                {trainerTasks?.unlocked && phase2Expanded && (
                    <div className="setup-checklist__steps">
                        {TRAINER_TASKS_STEPS.map((step) => {
                            const isDone = trainerTasks[step.key];
                            const isNext = step.key === nextTrainerStep;
                            const StepIcon = step.icon;
                            const stepRoute = getStepRoute(step);
                            return (
                                <div
                                    key={step.key}
                                    className={`setup-checklist__step ${isDone ? 'setup-checklist__step--done' : ''} ${isNext ? 'setup-checklist__step--next' : ''}`}
                                    onClick={!isDone && stepRoute ? () => navigate(stepRoute) : undefined}
                                    role={!isDone && stepRoute ? 'button' : undefined}
                                    tabIndex={!isDone && stepRoute ? 0 : undefined}
                                >
                                    <div className={`setup-checklist__check ${isDone ? 'setup-checklist__check--done' : isNext ? 'setup-checklist__check--next' : 'setup-checklist__check--pending'}`}>
                                        {isDone ? <FaCheck size={14} /> : <StepIcon size={16} />}
                                    </div>
                                    <div className="setup-checklist__step-info">
                                        <p className="setup-checklist__step-label">
                                            {isDone ? t(step.doneLabelKey) : t(step.labelKey)}
                                        </p>
                                        <p className="setup-checklist__step-description">
                                            {isDone ? t('setup.done') : t(step.descKey)}
                                        </p>
                                    </div>
                                    {!isDone && stepRoute && (
                                        <div className="setup-checklist__step-actions">
                                            <button
                                                className={`setup-checklist__step-btn ${isNext ? 'setup-checklist__step-btn--primary' : ''}`}
                                                onClick={(e) => { e.stopPropagation(); navigate(stepRoute); }}
                                            >
                                                {t(step.btnTextKey)}
                                                <FaArrowRight size={11} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SetupChecklist;
